import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import { MuiChipsInput } from "mui-chips-input";
import {
  EditorState,
  ContentState,
  convertFromHTML,
  convertToRaw,
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { TopNavBar } from "../../components/navbars/topNavbar/topNavbar";
import { BlogUseStyles } from "../createBlog/style";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { AlertProps, Message, NetWorkCallMethods } from "../../utils";
import { NetworkCall } from "../../networkcall";
import { config } from "../../config";
import { AlertContext } from "../../contexts";
import { Routes } from "../../router/routes";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import { imageUploads } from "../blogs/utils";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import Upload from "../createBlog/uploadImage";

const AddAuthors = () => {
  const classes = BlogUseStyles();
  const alert = React.useContext(AlertContext);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const history = useHistory();
  const state = useLocation();
  const [formValues, setFormValues] = useState({
    name: "",
    author_url: "",
    linkedin_url: "",
    description: "",
    facebook_url: "",
    twitter_url: "",
    avatar_image_url: "",
    error: { file_image: "" },
    isLoading: false,
    status: false,
  });
  const [options, setOptions] = React.useState([]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleStatusChange = (event) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      status: event.target.checked,
    }));
  };

  const handleAuthorFileChange = async (e) => {
    const file = e?.target?.files[0];

    if (!file) {
      return; // Exit function if no file is selected
    }

    const fileSizeInBytes = file.size;
    const fileSizeInMegabytes = fileSizeInBytes / (1024 * 1024);

    if (fileSizeInMegabytes < 4) {
      try {
        const updateTheImage = await imageUploads(file);

        setFormValues((prevValues) => ({
          ...prevValues,
          avatar_image_url: updateTheImage?.response, // Update banner_file_url instead of file_url
          error: { ...prevValues.error, avatar_image_url: "" }, // Update error message key
        }));

        // Additional logic if needed after successful upload
      } catch (error) {
        console.log("Error uploading banner image:", error);
        // Handle error if upload fails
      }
    } else {
      // File size exceeds limit, update error message
      setFormValues((prevValues) => ({
        ...prevValues,
        error: {
          ...prevValues.error,
          avatar_image_url: "Upload Banner Image below 3 MB",
        }, // Update error message key
      }));
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Validation checks
    const errors = {};
    let isValid = true;

    if (!formValues.name.trim()) {
      errors.title = "Title is required";
      isValid = false;
    }

    if (!isValid) {
      setFormValues((prevValues) => ({
        ...prevValues,
        error: errors,
      }));
      return;
    }

    // If form is valid, proceed with form submission
    if (state?.state?.id) {
      updateAuthor();
    } else {
      addAuthor();
    }
  };

  const addAuthor = () => {
    let payload = {
      name: formValues.name,
      author_url: formValues.author_url,
      linkedin_url: formValues.linkedin_url,
      description: formValues.description,
      twitter_url: formValues.twitter_url,
      facebook_url: formValues.facebook_url,
      avatar_image_url:
        formValues.avatar_image_url === "" ? null : formValues.avatar_image_url,
      is_active: formValues.status,
    };
    NetworkCall(
      `${config.api_feature}api/v1/authors/create_author`,
      NetWorkCallMethods.post,
      payload,
      null,
      true,
      true
    )
      .then((res) => {
        setIsButtonDisabled(true);

        window.location.href = Routes.authorList;
      })
      .catch((err) => {
        console.log(err);

        if (err?.response?.status === 409) {
          alert.setSnack({
            ...alert,
            open: true,
            severity: AlertProps.severity.error,
            msg: err?.response?.data?.message,
          });
          return;
        } else {
          alert.setSnack({
            ...alert,
            open: true,
            severity: AlertProps.severity.error,
            msg: Message.BlogErr,
          });
        }
      });
  };

  const updateAuthor = () => {
    let payload = {
      id: state?.state?.id,
      name: formValues.name,
      author_url: formValues.author_url,
      linkedin_url: formValues.linkedin_url,
      description: formValues.description,
      twitter_url: formValues.twitter_url,
      facebook_url: formValues.facebook_url,
      avatar_image_url:
        formValues.avatar_image_url === "" ? null : formValues.avatar_image_url,
      is_active: formValues.status,
    };
    NetworkCall(
      `${config.api_feature}api/v1/authors/edit_author`,
      NetWorkCallMethods.post,
      payload,
      null,
      true,
      false
    )
      .then((res) => {
        setIsButtonDisabled(true);

        window.location.href = Routes.authorList;
      })
      .catch((err) => {
        console.log(err);

        if (err?.response?.status === 409) {
          alert.setSnack({
            ...alert,
            open: true,
            severity: AlertProps.severity.error,
            msg: err?.response?.data?.message,
          });
          return;
        } else {
          alert.setSnack({
            ...alert,
            open: true,
            severity: AlertProps.severity.error,
            msg: Message.BlogErr,
          });
        }
      });
  };

  useEffect(() => {
    if (state?.state?.id) {
      editAuthor();
    }
  }, []);

  const editAuthor = () => {
    let payload = {
      id: state?.state?.id,
    };
    NetworkCall(
      `${config.api_feature}api/v1/authors/edit_author`,
      NetWorkCallMethods.post,
      payload,
      null,
      true,
      false
    )
      .then((res) => {
        setFormValues((prevValues) => ({
          ...prevValues,
          name: res?.data?.updated_author?.name,
          description: res?.data?.updated_author?.description,
          linkedin_url: res?.data?.updated_author?.linkedin_url,
          twitter_url: res?.data?.updated_author?.twitter_url,
          avatar_image_url: res?.data?.updated_author?.avatar_image_url,
          facebook_url: res?.data?.updated_author?.facebook_url,
          status: res?.data?.updated_author?.is_active,
        }));
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <div>
      <TopNavBar
        title="Provision to Add and Edit Authors"
        buttonLabel={false}
        disables={isButtonDisabled}
      />
      <form onSubmit={handleFormSubmit} style={{ height: "90vh" }}>
        <Grid p={2} container spacing={2} alignItems="stretch">
          <Grid textAlign={"right"} item xs={12}>
            <Box>
              <button className={classes.button} type="submit">
                {state?.state?.id ? "Update" : "Submit"}
              </button>
            </Box>
          </Grid>
          {/* Left grid for Title, Category, and Hashtags */}
          <Grid item lg={7} md={7} sm={7} xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box>
                  <TextField
                    label="Author Name"
                    name="name"
                    value={formValues.name}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <TextField
                    label="Author Linkedin url"
                    name="linkedin_url"
                    value={formValues.linkedin_url}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box>
                  <TextField
                    label="Author Description"
                    name="description"
                    value={formValues.description}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box>
                  <TextField
                    label="Author Twitter url"
                    name="twitter_url"
                    value={formValues.twitter_url}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box>
                  <TextField
                    label="Author Facbook url"
                    name="facebook_url"
                    value={formValues.facebook_url}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formValues?.status}
                        onChange={handleStatusChange}
                      />
                    }
                    label={"Active"}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Right grid for Upload image */}
          <Grid item lg={5} md={5} sm={5} xs={12}>
            <Box
              display="grid"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              {/* Banner Image Section */}
              {!formValues.avatar_image_url ? (
                <Button
                  component="label"
                  role={undefined}
                  variant="outlined"
                  tabIndex={-1}
                  startIcon={<CloudUploadIcon />}
                >
                  Upload Author Image
                  <input
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleAuthorFileChange}
                  />
                </Button>
              ) : (
                <>
                  <img
                    src={formValues.avatar_image_url}
                    alt="banner"
                    style={{
                      width: "100%",
                      height: "260px",
                      paddingBottom: "10px",
                    }}
                  />
                  <Box display="flex" justifyContent="center">
                    <Button
                      onClick={() =>
                        setFormValues((prevValues) => ({
                          ...prevValues,
                          avatar_image_url: "",
                        }))
                      }
                      style={{
                        backgroundColor: "transparent",
                        color: "red",
                        padding: "2px 4px",
                        fontSize: "13px",
                        fontWeight: "bold",
                        textTransform: "none",
                        border: "1px solid rgba(0, 0, 0, 0.1)",
                        cursor: "pointer",
                      }}
                    >
                      Remove Author Image
                    </Button>
                  </Box>
                </>
              )}
              <Box height="20px" />
            </Box>
          </Grid>

          {/* Error message */}
          {formValues.error.file_image && (
            <Typography color="error">{formValues.error.file_image}</Typography>
          )}

          {/* Submit button */}
        </Grid>
      </form>
    </div>
  );
};

export default AddAuthors;
